import GoogleMap from 'google-maps-react-markers';
import { useRef, useState, useImperativeHandle, forwardRef, useEffect, useCallback, useMemo } from 'react';
import { PUBLIC_ENV } from 'utils/publicEnv';
import useSupercluster from 'use-supercluster';
import { BBox, GeoJsonProperties } from 'geojson';
import * as Supercluster from 'supercluster';
import { useGetOrganisationGeolocationsQuery } from 'generated/graphql';
import { Box, LinearProgress } from '@mui/material';
import { useOrganisationPersonalWebshop } from 'hooks/useOrganisationPersonalWebshop';
import FormError from 'components/Forms/FormError';

import useMarket from 'stores/useMarket';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { MapMarker } from './MapMarker';
import { ConsultantPopup } from './ConsultantPopup';

function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
    const toRad = (x: number) => (x * Math.PI) / 180;
    const R = 6371; // Earth radius in km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const d = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(d), Math.sqrt(1 - d));

    return R * c;
}

type MapData = {
    bounds?: google.maps.LatLngBounds;
    center: (number | undefined)[];
    zoom: number;
};

function useConsutlantMapDefaultSettings() {
    const {
        coordinatesCenter,
        defaultMapZoom,
    } = useMarket((marketState) => ({
        coordinatesCenter: marketState.coordinatesCenter,
        defaultMapZoom: marketState.defaultMapZoom,
    }));

    const defaultSettings: MapData = {
        bounds: undefined,
        center: Object.values(coordinatesCenter),
        zoom: defaultMapZoom,
    };

    return { defaultSettings, defaultCenter: coordinatesCenter };
}

export type ConsultantMapHandle = {
    panTo: (lat: number, lng: number, zoom?: number) => void;
    fitBounds: (bounds: google.maps.LatLngBounds) => void;
    fitBoundsToMarkers: () => void;
    focusConsultant: (organisationId: number, zoom?: number) => void;
};

type ConsultantMapProps = {
    onMapMarkerClicked: (consultantIds: number) => void;
    onVisibleConsultantsChange: (consultants: Array<{ id: number; distance?: number }>) => void;
    searchCenter: { lat: number; lng: number } | null;
    fallbackConsultantIds: number[];
    textSearchResultIds: number[];
    textSearchResults?: Array<{
        id: number;
        first_name: string;
        last_name: string;
        shipping_city: string;
        shipping_postal_code: string;
        profile_photo_path?: string | null;
        profile_url: string;
    }>;
    clickedResult?: {
        id: number;
        first_name: string;
        last_name: string;
        shipping_city: string;
        shipping_postal_code: string;
        profile_photo_path?: string | null;
        profile_url: string;
    };
    onUserInteraction: (isInteracting: boolean) => void;
};

export const ConsultantMap = forwardRef<ConsultantMapHandle, ConsultantMapProps>(
    ({ onMapMarkerClicked, onVisibleConsultantsChange, searchCenter, fallbackConsultantIds, textSearchResultIds, textSearchResults, clickedResult, onUserInteraction }, ref) => {
        const { t } = useServerTranslation();
        const mapRef = useRef<google.maps.Map>();

        const { defaultSettings, defaultCenter } = useConsutlantMapDefaultSettings();
        const [map, setMap] = useState<MapData>(defaultSettings);
        const [popupData, setPopupData] = useState<{
            lat: number;
            lng: number;
            consultant: any;
        } | null>(null);

        const market = useMarket((state) => state.market);

        const { data, previousData, loading, error } = useGetOrganisationGeolocationsQuery({ variables: { market } });

        const geolocations = data?.organisationGeolocations || previousData?.organisationGeolocations;

        const organisationCoordinates = useMemo(() => geolocations?.items ?? [], [geolocations?.items]);

        // Filter coordinates based on fallback, text search results, or search center
        const filteredCoordinates = useMemo(() => {
            if (fallbackConsultantIds.length > 0) {
                // Show only fallback consultants
                return organisationCoordinates.filter((c) => fallbackConsultantIds.includes(c.organisation_id));
            } if (searchCenter) {
                // For geographic searches, show consultants within viewport bounds
                return organisationCoordinates.filter((c) => map.bounds
                    && c.latitude >= map.bounds.getSouthWest()!.lat()
                    && c.latitude <= map.bounds.getNorthEast()!.lat()
                    && c.longitude >= map.bounds.getSouthWest()!.lng()
                    && c.longitude <= map.bounds.getNorthEast()!.lng());
            } if (textSearchResultIds.length > 0) {
                // Show only text search results (for pure name searches without geocoding)
                return organisationCoordinates.filter((c) => textSearchResultIds.includes(c.organisation_id));
            }

            return organisationCoordinates;
        }, [organisationCoordinates, fallbackConsultantIds, searchCenter, textSearchResultIds, map.bounds]);

        const geoJsonCoordinates: Array<Supercluster.PointFeature<GeoJsonProperties>> = filteredCoordinates
            .map((coordinate) => ({
                type: 'Feature',
                properties: { cluster: false, organisation_id: coordinate.organisation_id },
                geometry: {
                    type: 'Point',
                    coordinates: [coordinate.longitude, coordinate.latitude],
                },
            }));

        const mapBounds: BBox = [
            map.bounds?.getSouthWest()?.lng()!,
            map.bounds?.getSouthWest()?.lat()!,
            map.bounds?.getNorthEast()?.lng()!,
            map.bounds?.getNorthEast()?.lat()!,
        ];

        const { clusters, supercluster } = useSupercluster({
            points: geoJsonCoordinates,
            bounds: mapBounds,
            zoom: map.zoom,
            options: { radius: 75, maxZoom: 20 },
        });

        const isLoading = loading && !geolocations;
        const errorMessage = error?.message;

        const updateVisibleConsultants = useCallback(() => {
            if (map.bounds && filteredCoordinates.length > 0) {
                // Calculate distance from search center when available (e.g., fallback), otherwise from map center
                const mapCenter = {
                    lat: (map.bounds.getSouthWest().lat() + map.bounds.getNorthEast().lat()) / 2,
                    lng: (map.bounds.getSouthWest().lng() + map.bounds.getNorthEast().lng()) / 2,
                };
                const centerForDistance = searchCenter ?? mapCenter;

                const visibleConsultants = filteredCoordinates
                    .map((c) => {
                        const distance = haversine(centerForDistance, { lat: c.latitude, lng: c.longitude });

                        return { id: c.organisation_id, distance };
                    })
                    .sort((a, b) => a.distance - b.distance);
                onVisibleConsultantsChange(visibleConsultants);
            }
        }, [map.bounds, filteredCoordinates, onVisibleConsultantsChange, searchCenter]);

        // Recalculate visible consultants when map changes
        useEffect(() => {
            updateVisibleConsultants();
        }, [updateVisibleConsultants]);

        useEffect(() => {
            if (fallbackConsultantIds.length > 0 && organisationCoordinates.length > 0) {
                const fallbackCoords = organisationCoordinates.filter((c) => fallbackConsultantIds.includes(c.organisation_id));

                if (fallbackCoords.length > 0) {
                    setTimeout(() => {
                        const fallbackBounds = new google.maps.LatLngBounds();
                        fallbackCoords.forEach((consultant) => {
                            fallbackBounds.extend(new google.maps.LatLng(consultant.latitude, consultant.longitude));
                        });

                        const padding = 0.1; // 0.1 degrees padding
                        const ne = fallbackBounds.getNorthEast();
                        const sw = fallbackBounds.getSouthWest();
                        fallbackBounds.extend(new google.maps.LatLng(ne.lat() + padding, ne.lng() + padding));
                        fallbackBounds.extend(new google.maps.LatLng(sw.lat() - padding, sw.lng() - padding));

                        mapRef.current?.fitBounds(fallbackBounds);

                        if (mapRef.current) {
                            const listener = google.maps.event.addListener(mapRef.current, 'bounds_changed', () => {
                                if (mapRef.current && mapRef.current.getZoom()! > 12) {
                                    mapRef.current.setZoom(12);
                                }
                                google.maps.event.removeListener(listener);
                            });
                        }
                    }, 100);
                }
            }
        }, [fallbackConsultantIds, organisationCoordinates]);

        const updateConsultantData = useCallback((lat: number, lng: number, organisationId?: number) => {
            // Find consultant from clicked organisation results or text search results
            const consultant = [
                ...(clickedResult ? [clickedResult] : []),
                ...(textSearchResults ?? []),
            ].find((c) => c.id === organisationId);

            if (mapRef.current) {
                const consultantData = consultant || {
                    id: organisationId,
                    first_name: 'Consultant',
                    last_name: `#${organisationId}`,
                    shipping_city: 'Unknown',
                    shipping_postal_code: '',
                    profile_photo_path: null,
                    profile_url: `#${organisationId}`,
                };

                setPopupData({
                    lat,
                    lng,
                    consultant: consultantData,
                });
            }
        }, [clickedResult, textSearchResults]);

        useEffect(() => {
            if (clickedResult && popupData && clickedResult !== popupData?.consultant) {
                updateConsultantData(popupData.lat, popupData.lng, clickedResult.id);
            }
        }, [clickedResult, popupData, updateConsultantData]);

        const onMarkerClick = (lat: number, lng: number, organisationId?: number, clusterId?: number) => {
            if (clusterId) {
                const expansionZoom = Math.min(supercluster!.getClusterExpansionZoom(clusterId), 20);
                mapRef.current?.setZoom(expansionZoom);
                mapRef.current?.panTo({ lat, lng });
                setPopupData(null);

                return;
            }
            if (!organisationId) {
                return;
            }

            updateConsultantData(lat, lng, organisationId);

            if (map.zoom < 6) {
                mapRef.current?.setZoom(map.zoom + 1);
            }
            onMapMarkerClicked(organisationId);
            mapRef.current?.setCenter({ lat, lng });
        };

        const onGoogleApiLoaded = ({ map: initialMap }: { map: google.maps.Map }) => {
            if (initialMap) {
                mapRef.current = initialMap;
                setMap({
                    bounds: initialMap.getBounds()!,
                    center: [initialMap.getCenter()!.lat(), initialMap.getCenter()!.lng()],
                    zoom: initialMap.getZoom()!,
                });

                let interactionTimeout: NodeJS.Timeout | null = null;

                const startInteraction = () => {
                    onUserInteraction(true);
                    if (interactionTimeout) {
                        clearTimeout(interactionTimeout);
                        interactionTimeout = null;
                    }
                };

                const endInteraction = () => {
                    onUserInteraction(false);
                };

                google.maps.event.addListener(initialMap, 'dragstart', startInteraction);
                google.maps.event.addListener(initialMap, 'drag', startInteraction);
                google.maps.event.addListener(initialMap, 'zoom_changed', startInteraction);

                google.maps.event.addListener(initialMap, 'dragend', () => {
                    if (interactionTimeout) clearTimeout(interactionTimeout);
                    interactionTimeout = setTimeout(endInteraction, 200);
                });
                google.maps.event.addListener(initialMap, 'idle', () => {
                    if (interactionTimeout) clearTimeout(interactionTimeout);
                    interactionTimeout = setTimeout(endInteraction, 200);
                });

                if (initialMap.getBounds()) {
                    let consultantsToShow = organisationCoordinates;

                    // Only filter by viewport if we have a search center (geocodable location)
                    if (searchCenter) {
                        consultantsToShow = organisationCoordinates.filter((c) => c.latitude >= initialMap.getBounds()!.getSouthWest()!.lat()
                            && c.latitude <= initialMap.getBounds()!.getNorthEast()!.lat()
                            && c.longitude >= initialMap.getBounds()!.getSouthWest()!.lng()
                            && c.longitude <= initialMap.getBounds()!.getNorthEast()!.lng());
                    }

                    // Calculate distance from current map center
                    const mapCenter = {
                        lat: (initialMap.getBounds()!.getSouthWest()!.lat() + initialMap.getBounds()!.getNorthEast()!.lat()) / 2,
                        lng: (initialMap.getBounds()!.getSouthWest()!.lng() + initialMap.getBounds()!.getNorthEast()!.lng()) / 2,
                    };

                    const centerForDistance = searchCenter ?? mapCenter;
                    const visibleConsultants = consultantsToShow
                        .map((c) => {
                            const distance = haversine(centerForDistance, { lat: c.latitude, lng: c.longitude });

                            return { id: c.organisation_id, distance };
                        })
                        .sort((a, b) => a.distance - b.distance);
                    onVisibleConsultantsChange(visibleConsultants);
                }
            }
        };

        useImperativeHandle(ref, () => ({
            panTo: (lat: number, lng: number, zoom: number = 11) => {
                if (mapRef.current) {
                    mapRef.current.setCenter({ lat, lng });
                    mapRef.current.setZoom(zoom);
                }
            },
            fitBounds: (bounds: google.maps.LatLngBounds) => {
                if (mapRef.current) {
                    mapRef.current.fitBounds(bounds);
                }
            },
            fitBoundsToMarkers: () => {
                if (mapRef.current && organisationCoordinates.length > 0) {
                    const bounds = new google.maps.LatLngBounds();
                    organisationCoordinates.forEach((coordinate) => {
                        bounds.extend(new google.maps.LatLng(coordinate.latitude, coordinate.longitude));
                    });

                    const padding = 0.1;
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    bounds.extend(new google.maps.LatLng(ne.lat() + padding, ne.lng() + padding));
                    bounds.extend(new google.maps.LatLng(sw.lat() - padding, sw.lng() - padding));

                    mapRef.current.fitBounds(bounds);

                    const listener = google.maps.event.addListener(mapRef.current, 'bounds_changed', () => {
                        if (mapRef.current && mapRef.current.getZoom()! > 10) {
                            mapRef.current.setZoom(10);
                        }
                        google.maps.event.removeListener(listener);
                    });
                }
            },
            focusConsultant: (organisationId: number, zoom: number = 12) => {
                if (!mapRef.current) return;
                const target = organisationCoordinates.find((c) => c.organisation_id === organisationId);
                if (!target) return;

                const lat = target.latitude;
                const lng = target.longitude;
                mapRef.current.setCenter({ lat, lng });
                mapRef.current.setZoom(zoom);

                updateConsultantData(lat, lng, organisationId);
            },
        }));

        const handleClosePopup = useCallback(() => {
            setPopupData(null);
        }, []);

        const { baseUrl } = useOrganisationPersonalWebshop();

        const handleViewProfile = useCallback((profileUrl: string) => {
            try {
                const profilePath = new URL(profileUrl || '').pathname.replace(/\/sv/g, '');
                const href = `${baseUrl}${profilePath}`;
                window.open(href, '_blank');
            } catch {
                window.open(profileUrl, '_blank');
            }
        }, [baseUrl]);

        return (
            <Box sx={{ width: '100%', height: '600px' }}>
                {isLoading && <LinearProgress />}
                {errorMessage && <FormError>{t('findconsultant.error-loading-map-markers')}</FormError>}
                {!errorMessage && !isLoading && (
                    <GoogleMap
                        defaultCenter={defaultCenter}
                        defaultZoom={defaultSettings.zoom}
                        apiKey={PUBLIC_ENV.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}
                        onGoogleApiLoaded={onGoogleApiLoaded}
                        options={{
                            clickableIcons: false,
                            streetViewControl: false,
                            fullscreenControl: false,
                            mapTypeControl: false,

                        }}
                        onChange={(mapData) => {
                            setMap(mapData);
                        }}
                    >
                        {map && clusters.map(({ geometry, properties }, index) => (
                            <MapMarker
                                key={index}
                                clusterId={properties?.cluster_id}
                                lat={geometry.coordinates[1]}
                                lng={geometry.coordinates[0]}
                                onClick={onMarkerClick}
                                zoom={map.zoom}
                                organisationId={properties?.organisation_id}
                                zIndex={1}
                            />
                        ))}
                        {popupData && (
                            <ConsultantPopup
                                lat={popupData.lat}
                                lng={popupData.lng}
                                zIndex={99999}
                                consultant={popupData.consultant}
                                onClose={handleClosePopup}
                                onViewProfile={handleViewProfile}
                            />
                        )}
                    </GoogleMap>
                )}
            </Box>
        );
    },
);
